import { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { useCountdown } from '../../../hooks/useCountdown';
import { getToken, recordActivity, setSession } from '../../../utils/session';
// react-router-dom components
import { useNavigate } from 'react-router-dom';

// @mui material components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

// Authentication layout components
import BasicLayout from 'layouts/authentication/components/BasicLayout';

// Images
import bgImage from 'assets/images/front-view-doctor-holding-stethoscope.jpg';
import logoImage from 'assets/images/logoIRM2.png';

const OTP_RESEND_COOLDOWN_SECONDS = 120;

function Basic() {
  // const [rememberMe, setRememberMe] = useState(false);
  const [mode, setMode] = useState('password'); // 'password' | 'otp-username' | 'otp-code'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // OTP-mode state
  const [otpUsername, setOtpUsername] = useState('');
  const [otpAccessToken, setOtpAccessToken] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpInfo, setOtpInfo] = useState(null);
  const countdown = useCountdown();

  const { user } = useApi();

  useEffect(() => {
    if (getToken()) {
      navigate('/dashboard', { replace: true });
    }
  }, []);

  // Shared by password login and OTP-verify success so both paths behave
  // identically downstream (localStorage, user context, redirect).
  const completeLogin = (data) => {
    const fullName = [data.data.first_name, data.data.middle_name, data.data.last_name]
      .filter(Boolean)
      .join(' ');
    const userData = {
      user_id: data.data.user_id,
      is_super_admin: data.data.is_super_admin,
      user_type_id: data.data.user_type_id,
      user_type_name: data.data.user_type_name,
      user_name: username || otpUsername,
      name: fullName || data.data.name || username || otpUsername,
    };

    setSession({
      token: data.data.token,
      refresh_token: data.data.refresh_token,
      user: userData,
      session_config: data.data.session_config,
    });
    recordActivity();
    window.dispatchEvent(new Event('auth:login'));

    navigate('/dashboard');
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    try {
      setError(null);
      setLoading(true);
      const data = await user.login(username, password);
      setLoading(false);

      // Username/password were correct but this account has 2FA/OTP login
      // enabled — password alone isn't enough. Hand off to the same OTP
      // entry screen used by the standalone "Login via OTP" flow.
      if (data.data?.requires_otp) {
        setOtpAccessToken(data.data.otp_access_token);
        setOtpInfo(data.message);
        setOtpCode('');
        setMode('otp-code');
        countdown.start(OTP_RESEND_COOLDOWN_SECONDS);
        return;
      }

      completeLogin(data);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    }
  };

  const handleSendOtp = async (e) => {
    if (e) {
      e.preventDefault();
    }

    try {
      setError(null);
      setLoading(true);
      const data = await user.requestOtp(otpUsername);
      setLoading(false);
      setOtpAccessToken(data.data.otp_access_token);
      setOtpInfo(data.message);
      setOtpCode('');
      setMode('otp-code');
      countdown.start(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Failed to send OTP');
    }
  };

  const handleResendOtp = async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await user.resendOtp(otpAccessToken);
      setLoading(false);
      setOtpInfo(data.message);
      countdown.start(OTP_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setLoading(false);
      // Don't blindly restart the short resend countdown here — if the user
      // has been locked out for entering wrong OTPs too many times, the
      // server's message states the actual (much longer) wait in minutes,
      // and restarting a 2-minute timer would misrepresent that.
      setError(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) {
      e.preventDefault();
    }

    try {
      setError(null);
      setLoading(true);
      const data = await user.verifyOtp(otpAccessToken, otpCode);
      setLoading(false);
      completeLogin(data);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Invalid or expired OTP');
    }
  };

  const switchToOtpUsernameMode = () => {
    setError(null);
    setOtpUsername('');
    setOtpAccessToken(null);
    setOtpCode('');
    setOtpInfo(null);
    setMode('otp-username');
  };

  const switchToPasswordMode = () => {
    setError(null);
    setOtpAccessToken(null);
    setOtpCode('');
    setOtpInfo(null);
    setMode('password');
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="logo"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography
            variant="h4"
            fontWeight="medium"
            color="white"
            mt={1}
            sx={{ padding: '0px 15px' }}
          >
            <img src={logoImage} width={'100%'} height={80} />
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}></Grid>
        </MDBox>
        {error && (
          <Alert severity="error" sx={{ margin: '10px 20px 0px 20px' }}>
            {error}
          </Alert>
        )}
        {otpInfo && mode === 'otp-code' && (
          <Alert severity="info" sx={{ margin: '10px 20px 0px 20px' }}>
            {otpInfo}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {loading && <CircularProgress color="logo" />}
        </Box>
        <MDBox pt={4} pb={3} px={3}>
          {mode === 'password' && (
            <MDBox component="div" role="form">
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Username"
                  fullWidth
                  onChange={(e) => setUsername(e.target.value)}
                  value={username}
                  required
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="logo"
                  fullWidth
                  onClick={handleSubmit}
                  type="button"
                >
                  sign in
                </MDButton>
              </MDBox>
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  color="info"
                  sx={{ cursor: 'pointer' }}
                  onClick={switchToOtpUsernameMode}
                >
                  Login via OTP
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {mode === 'otp-username' && (
            <MDBox component="div" role="form">
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Username"
                  fullWidth
                  onChange={(e) => setOtpUsername(e.target.value)}
                  value={otpUsername}
                  required
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="logo"
                  fullWidth
                  onClick={handleSendOtp}
                  type="button"
                >
                  send otp
                </MDButton>
              </MDBox>
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  color="text"
                  sx={{ cursor: 'pointer' }}
                  onClick={switchToPasswordMode}
                >
                  Back to password login
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {mode === 'otp-code' && (
            <MDBox component="div" role="form">
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Enter OTP"
                  fullWidth
                  inputProps={{ maxLength: 6 }}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  value={otpCode}
                  required
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="logo"
                  fullWidth
                  onClick={handleVerifyOtp}
                  type="button"
                >
                  verify
                </MDButton>
              </MDBox>
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  color={countdown.isActive ? 'text' : 'info'}
                  sx={{ cursor: countdown.isActive ? 'default' : 'pointer' }}
                  onClick={countdown.isActive ? undefined : handleResendOtp}
                >
                  {countdown.isActive
                    ? `Resend OTP (${countdown.secondsLeft}s)`
                    : 'Resend OTP'}
                </MDTypography>
              </MDBox>
              <MDBox mt={1} textAlign="center">
                <MDTypography
                  variant="button"
                  color="text"
                  sx={{ cursor: 'pointer' }}
                  onClick={switchToPasswordMode}
                >
                  Back to password login
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
